/**
 * Места — слой действий года (interactives.md H2; core-loop-v2 §2–3;
 * план 2d, Task 3).
 *
 * Каждое место — несколько дел. Дело либо даёт прямой исход
 * `MinigameOutcome` (его применяет движок через `applyActions`, и там же
 * списывается очко энергии и считается убывающая отдача), либо ведёт в
 * мини-игру раздела E/F/G по `minigameId` — тогда клиент играет её и отдаёт
 * исход тем же путём. Жизнь здесь не мутируется и не меняется: только
 * доступность и расчёт исхода.
 *
 * Доступность (`placeAvailability`, `actionAvailability`) отвечает ключом
 * причины для интерфейса: нет энергии (`place.reason.noEnergy`), место
 * исчерпано за год (`place.reason.enough`), возраст, страна, деньги…
 * Азартные места («risk») закрыты в учебном режиме (`gamblingEnabled: false`).
 *
 * Числа исходов работы, зала и отдыха — константы POLICY_* движка: автопилот
 * (`expectedPolicy`) тратит энергию в тех же местах с теми же эффектами.
 *
 * Все жребии — через `Rng`; Math.random здесь нет.
 */
import { getValue, listCountries, type Dataset, type GamblingLaw } from '@life/data';
import {
  ABROAD_BASE_CHANCE,
  ABROAD_COST_GDP,
  ABROAD_MAX_AGE,
  ABROAD_MIN_AGE,
  ABROAD_MONEY_GDP,
  ABROAD_SPONSOR_BONUS,
  FLAG_ABROAD_DECIDED,
  FLAG_JOB_LEVEL_BONUS,
  FLAG_JOB_START_AGE,
  FLAG_SPONSOR,
  FLAG_TRAVELED,
  PLACE_WORK,
  POLICY_GYM_HAPPINESS,
  POLICY_GYM_HEALTH,
  POLICY_REST_HAPPINESS,
  POLICY_SHIFT_HEALTH,
  POLICY_SHIFT_INCOME_SHARE,
  visitMultiplier,
  visitsOf,
  type Life,
  type MinigameOutcome,
  type Rng,
} from '@life/engine';
import { CASINO_MIN_AGE, casinoAvailable, gdpPpp, lossPercent } from './casino/common';
import { DICE_EXPECTATION, DICE_MIN_AGE } from './casino/dice';
import { LOTTERY_RTP } from './casino/lottery';
import { ROULETTE_RTP } from './casino/roulette';
import { SLOTS_RTP } from './casino/slots';
import { BLACKJACK_RTP_BASIC } from './casino/blackjack';
import { BETS_MARGIN } from './casino/bets';
import { loans } from './money/loans';
import { stocks } from './money/stocks';
import { crypto } from './money/crypto';
import { TALK_MAX_CHILD_AGE, TALK_MIN_CHILD_AGE } from './life/talk';
import { youngestAgeOf } from './life/common';
import { BARGAIN_MAX_AGE, BARGAIN_MIN_AGE } from './skill/bargain';

export type PlaceId =
  | 'work'
  | 'jobcenter'
  | 'courses'
  | 'gym'
  | 'clinic'
  | 'home'
  | 'bar'
  | 'temple'
  | 'market'
  | 'bank'
  | 'travel'
  | 'migration'
  | 'rest'
  | 'casino'
  | 'lottery'
  | 'street'
  | 'bookmaker';

export const PLACE_IDS: readonly PlaceId[] = [
  'work',
  'jobcenter',
  'courses',
  'gym',
  'clinic',
  'home',
  'bar',
  'temple',
  'market',
  'bank',
  'travel',
  'migration',
  'rest',
  'casino',
  'lottery',
  'street',
  'bookmaker',
];

export type PlaceGroup = 'life' | 'money' | 'risk';

export type Availability = { ok: true } | { ok: false; reasonKey: string };

export interface PlaceOptions {
  law: GamblingLaw;
  /** `settings.gamblingEnabled`: в учебном режиме азартные места закрыты. */
  gamblingEnabled: boolean;
}

export interface PlaceAction {
  id: string;
  labelKey: string;
  /** Честное ожидание словами («скорее всего: здоровье ↑, деньги ↓»). */
  expectKey: string;
  /** Параметры текста ожидания (процент потерь, цена). */
  expectParams?: (life: Life, ds: Dataset) => Record<string, number>;
  /** Сколько стоит дело; без денег — `place.reason.noMoney`. */
  costMoney?: (life: Life, ds: Dataset) => number;
  /**
   * Мини-игра вместо прямого исхода: клиент (Host) запускает её по этому id
   * напрямую (`setup`/`play`), НЕ проверяя `MinigameDef.available` — условия
   * уже проверены здесь (`available` дела). Исход игры отдаётся в `applyActions`.
   */
  minigameId?: string;
  /** Для `minigameId`: играть ли мини-игру в этот визит (по умолчанию всегда); иначе — прямой `outcome`. */
  minigameWhen?: (life: Life) => boolean;
  /** Базовый эффект визита с мини-игрой: клиент складывает его с исходом игры (`mergeOutcomes`). */
  baseOutcome?: (life: Life, ds: Dataset, rng: Rng) => MinigameOutcome;
  /** Прямой исход, когда мини-игра не играется. */
  outcome?: (life: Life, ds: Dataset, rng: Rng) => MinigameOutcome;
  available: (life: Life, ds: Dataset, law: GamblingLaw) => Availability;
}

export interface PlaceDef {
  id: PlaceId;
  labelKey: string;
  group: PlaceGroup;
  actions: PlaceAction[];
  available: (life: Life, ds: Dataset, law: GamblingLaw, gamblingEnabled: boolean) => Availability;
}

export type PlaceRun = { kind: 'minigame'; id: string; outcome?: MinigameOutcome } | { kind: 'outcome'; outcome: MinigameOutcome };

// Ключи причин недоступности (i18n `place.reason.*`).
export const REASON_NO_ENERGY = 'place.reason.noEnergy';
export const REASON_ENOUGH = 'place.reason.enough';
export const REASON_AGE = 'place.reason.age';
export const REASON_ILLEGAL = 'place.reason.illegal';
export const REASON_DISABLED = 'place.reason.disabled';
export const REASON_CITY = 'place.reason.city';
export const REASON_NO_MONEY = 'place.reason.noMoney';
export const REASON_NO_JOB = 'place.reason.noJob';
export const REASON_HAS_JOB = 'place.reason.hasJob';
/** Стаж меньше двух лет: менять работу рано. */
export const REASON_SHORT_TENURE = 'place.reason.shortTenure';
export const REASON_NO_STRENGTH = 'place.reason.noStrength';
export const REASON_NO_FAMILY = 'place.reason.noFamily';
export const REASON_TOO_SOON = 'place.reason.tooSoon';
export const REASON_NOT_YET = 'place.reason.notYet';
export const REASON_NO_NEED = 'place.reason.noNeed';
export const REASON_IN_SCHOOL = 'place.reason.inSchool';
export const REASON_DEAD = 'place.reason.dead';

// Числа из interactives.md H2.
export const WORK_VACATION_HAPPINESS = 3;
export const WORK_VACATION_INCOME_SHARE = 0.02;
export const JOBCENTER_MIN_AGE = 16;
export const JOBCENTER_CHANGE_MIN_YEARS = 2;
/** Бонус к уровню при следующем найме или смене (флаг `jobLevelBonus` движка). */
export const JOBCENTER_LEVEL_BONUS = 1;
export const PARTTIME_GDP_SHARE = 0.05;
export const PARTTIME_HEALTH = -1;
export const COURSES_MIN_AGE = 10;
export const COURSES_KNOWLEDGE = 2;
export const COURSES_COST_GDP = 0.01;
/** До этого возраста (не включая) учёба бесплатна в странах со школой ≥ COURSES_FREE_SCHOOL_YEARS. */
export const COURSES_FREE_MAX_AGE = 18;
export const COURSES_FREE_SCHOOL_YEARS = 10;
export const GYM_MIN_AGE = 10;
export const GYM_MIN_HEALTH = 30;
export const CLINIC_CHECKUP_GDP = 0.02;
export const CLINIC_CHECKUP_HEALTH = 1;
export const HOME_PARENTS_RELATIONS = 2;
export const HOME_PARENTS_HAPPINESS = 1;
export const HOME_PARTNER_RELATIONS = 3;
export const BAR_MIN_AGE = 16;
export const BAR_RELATIONS = 3;
export const BAR_HAPPINESS = 2;
/** Базовая вероятность начать пить за вечер; по стране масштабируется удовлетворённостью жизнью. */
export const BAR_ALCOHOL_P = 0.1;
export const BAR_ALCOHOL_P_MIN = 0.05;
export const BAR_ALCOHOL_P_MAX = 0.2;
/** Удовлетворённость жизнью 0–10, около которой вероятность равна базовой. */
export const BAR_SATISFACTION_MID = 5.5;
export const BAR_ALCOHOL_HEALTH = -3;
export const BAR_ALCOHOL_RELATIONS = -3;
export const TEMPLE_HAPPINESS = 2;
export const TEMPLE_RELATIONS = 1;
export const MARKET_MIN_AGE = BARGAIN_MIN_AGE;
export const BANK_MIN_AGE = 16;
export const TRAVEL_MIN_AGE = 16;
export const TRAVEL_COST_GDP = 0.3;
export const TRAVEL_HAPPINESS = 5;
export const MIGRATION_MIN_AGE = 18;
export const MIGRATION_EVERY_YEARS = 5;
/** Возраст последней подачи документов (`migration.apply`). */
export const FLAG_MIGRATION_APPLIED_AGE = 'migrationAppliedAge';

const ok: Availability = { ok: true };
function no(reasonKey: string): Availability {
  return { ok: false, reasonKey };
}

function labelKey(place: PlaceId, action?: string): string {
  return action === undefined ? `place.${place}` : `place.${place}.${action}`;
}

function expectKey(place: PlaceId, action: string): string {
  return `place.expect.${place}.${action}`;
}

function minAge(life: Life, age: number): Availability {
  return life.age >= age ? ok : no(REASON_AGE);
}

function always(): Availability {
  return ok;
}

function num(life: Life, key: string): number | null {
  const v = life.flags[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function indicator(ds: Dataset, life: Life, ind: 'gdpPpp' | 'oopHealth' | 'schoolYears' | 'lifeSatisfaction'): number {
  const v = getValue(ds, life.iso3, ind, life.birthYear + life.age).v;
  return Number.isFinite(v) ? v : 0;
}

/** Сложить два исхода: числа суммируются, флаги объединяются, запись журнала — от второго, если есть. */
export function mergeOutcomes(a: MinigameOutcome, b: MinigameOutcome | undefined): MinigameOutcome {
  if (!b) return a;
  const out: MinigameOutcome = {};
  const money = (a.moneyDelta ?? 0) + (b.moneyDelta ?? 0);
  if (a.moneyDelta !== undefined || b.moneyDelta !== undefined) out.moneyDelta = money;
  const urge = (a.urgeDelta ?? 0) + (b.urgeDelta ?? 0);
  if (a.urgeDelta !== undefined || b.urgeDelta !== undefined) out.urgeDelta = urge;
  if (a.stats || b.stats) {
    const stats: NonNullable<MinigameOutcome['stats']> = { ...(a.stats ?? {}) };
    for (const [k, v] of Object.entries(b.stats ?? {})) {
      if (typeof v !== 'number') continue;
      const key = k as keyof typeof stats;
      stats[key] = (stats[key] ?? 0) + v;
    }
    out.stats = stats;
  }
  if (a.flags || b.flags) out.flags = { ...(a.flags ?? {}), ...(b.flags ?? {}) };
  if (a.habits || b.habits) out.habits = { ...(a.habits ?? {}), ...(b.habits ?? {}) };
  const decisionOption = b.decisionOption ?? a.decisionOption;
  if (decisionOption !== undefined) out.decisionOption = decisionOption;
  const logKey = b.logKey ?? a.logKey;
  if (logKey !== undefined) out.logKey = logKey;
  const logParams = b.logKey ? b.logParams : a.logKey ? a.logParams : (b.logParams ?? a.logParams);
  if (logParams !== undefined) out.logParams = logParams;
  if (a.heavy || b.heavy) out.heavy = true;
  return out;
}

/** Вероятность начать пить за вечер в баре: базовая, выше в странах с низкой удовлетворённостью жизнью. */
export function barAlcoholP(ds: Dataset, life: Life): number {
  const satisfaction = indicator(ds, life, 'lifeSatisfaction');
  const scaled = BAR_ALCOHOL_P * (1 + (BAR_SATISFACTION_MID - satisfaction) / BAR_SATISFACTION_MID);
  return Math.min(BAR_ALCOHOL_P_MAX, Math.max(BAR_ALCOHOL_P_MIN, scaled));
}

function coursesCost(life: Life, ds: Dataset): number {
  const free = life.age < COURSES_FREE_MAX_AGE && indicator(ds, life, 'schoolYears') >= COURSES_FREE_SCHOOL_YEARS;
  return free ? 0 : COURSES_COST_GDP * gdpPpp(ds, life);
}

function checkupCost(life: Life, ds: Dataset): number {
  return Math.max(0, CLINIC_CHECKUP_GDP * gdpPpp(ds, life) * (indicator(ds, life, 'oopHealth') / 100));
}

function travelCost(life: Life, ds: Dataset): number {
  return TRAVEL_COST_GDP * gdpPpp(ds, life);
}

/** Порог денег для решения move.abroad (как `canAfford` движка). */
function migrationMoneyNeed(life: Life, ds: Dataset): number {
  return ABROAD_MONEY_GDP * gdpPpp(ds, life);
}

function canAffordMigration(life: Life, ds: Dataset): boolean {
  return Number.isFinite(life.money) && life.money >= migrationMoneyNeed(life, ds);
}

function migrationChance(life: Life): number {
  return Math.max(0, Math.min(1, ABROAD_BASE_CHANCE + (life.flags[FLAG_SPONSOR] === true ? ABROAD_SPONSOR_BONUS : 0)));
}

function jobTenure(life: Life): number | null {
  if (life.job === null) return null;
  const started = num(life, FLAG_JOB_START_AGE);
  return started === null ? 0 : Math.max(0, life.age - started);
}

function gamblingPlace(gamblingEnabled: boolean, inner: () => Availability): Availability {
  return gamblingEnabled ? inner() : no(REASON_DISABLED);
}

function lossExpect(rtp: number): (life: Life, ds: Dataset) => Record<string, number> {
  return () => ({ loss: lossPercent(rtp) });
}

function shiftOutcome(life: Life): MinigameOutcome {
  return {
    moneyDelta: Math.max(0, (life.job?.income ?? 0) * POLICY_SHIFT_INCOME_SHARE),
    stats: { health: POLICY_SHIFT_HEALTH },
    logKey: 'place.work.shift.done',
  };
}

export const PLACES: PlaceDef[] = [
  {
    id: 'work',
    labelKey: labelKey('work'),
    group: 'money',
    available: (life) => (life.job === null ? no(REASON_NO_JOB) : ok),
    actions: [
      {
        id: 'shift',
        labelKey: labelKey('work', 'shift'),
        expectKey: expectKey('work', 'shift'),
        minigameId: 'shift',
        minigameWhen: (life) => visitsOf(life, PLACE_WORK) === 0,
        baseOutcome: shiftOutcome,
        outcome: shiftOutcome,
        available: always,
      },
      {
        id: 'vacation',
        labelKey: labelKey('work', 'vacation'),
        expectKey: expectKey('work', 'vacation'),
        costMoney: (life) => Math.max(0, (life.job?.income ?? 0) * WORK_VACATION_INCOME_SHARE),
        outcome: (life) => ({
          moneyDelta: -Math.max(0, (life.job?.income ?? 0) * WORK_VACATION_INCOME_SHARE),
          stats: { happiness: WORK_VACATION_HAPPINESS },
          logKey: 'place.work.vacation.done',
        }),
        available: always,
      },
    ],
  },
  {
    id: 'jobcenter',
    labelKey: labelKey('jobcenter'),
    group: 'money',
    available: (life) => minAge(life, JOBCENTER_MIN_AGE),
    actions: [
      {
        id: 'find',
        labelKey: labelKey('jobcenter', 'find'),
        expectKey: expectKey('jobcenter', 'find'),
        minigameId: 'interview',
        minigameWhen: (life) => life.pendingDecision?.kind === 'work.first' || life.pendingDecision?.kind === 'work.change',
        outcome: () => ({ flags: { [FLAG_JOB_LEVEL_BONUS]: JOBCENTER_LEVEL_BONUS }, logKey: 'place.jobcenter.found' }),
        available: (life) => (life.job !== null ? no(REASON_HAS_JOB) : life.inSchool ? no(REASON_IN_SCHOOL) : ok),
      },
      {
        id: 'change',
        labelKey: labelKey('jobcenter', 'change'),
        expectKey: expectKey('jobcenter', 'change'),
        minigameId: 'interview',
        minigameWhen: (life) => life.pendingDecision?.kind === 'work.change',
        outcome: () => ({ flags: { [FLAG_JOB_LEVEL_BONUS]: JOBCENTER_LEVEL_BONUS }, logKey: 'place.jobcenter.changed' }),
        available: (life) => {
          const tenure = jobTenure(life);
          if (tenure === null) return no(REASON_NO_JOB);
          return tenure >= JOBCENTER_CHANGE_MIN_YEARS ? ok : no(REASON_SHORT_TENURE);
        },
      },
      {
        id: 'parttime',
        labelKey: labelKey('jobcenter', 'parttime'),
        expectKey: expectKey('jobcenter', 'parttime'),
        outcome: (life, ds) => ({
          moneyDelta: Math.max(0, PARTTIME_GDP_SHARE * gdpPpp(ds, life)),
          stats: { health: PARTTIME_HEALTH },
          logKey: 'place.jobcenter.parttime.done',
        }),
        available: always,
      },
    ],
  },
  {
    id: 'courses',
    labelKey: labelKey('courses'),
    group: 'life',
    available: (life) => minAge(life, COURSES_MIN_AGE),
    actions: [
      {
        id: 'study',
        labelKey: labelKey('courses', 'study'),
        expectKey: expectKey('courses', 'study'),
        costMoney: coursesCost,
        outcome: (life, ds) => {
          const cost = coursesCost(life, ds);
          return { moneyDelta: cost > 0 ? -cost : 0, stats: { knowledge: COURSES_KNOWLEDGE }, logKey: 'place.courses.studied' };
        },
        available: always,
      },
    ],
  },
  {
    id: 'gym',
    labelKey: labelKey('gym'),
    group: 'life',
    available: (life) => {
      const age = minAge(life, GYM_MIN_AGE);
      if (!age.ok) return age;
      return life.stats.health < GYM_MIN_HEALTH ? no(REASON_NO_STRENGTH) : ok;
    },
    actions: [
      {
        id: 'train',
        labelKey: labelKey('gym', 'train'),
        expectKey: expectKey('gym', 'train'),
        outcome: () => ({ stats: { health: POLICY_GYM_HEALTH, happiness: POLICY_GYM_HAPPINESS }, logKey: 'place.gym.trained' }),
        available: always,
      },
    ],
  },
  {
    id: 'clinic',
    labelKey: labelKey('clinic'),
    group: 'life',
    available: always,
    actions: [
      {
        id: 'checkup',
        labelKey: labelKey('clinic', 'checkup'),
        expectKey: expectKey('clinic', 'checkup'),
        costMoney: checkupCost,
        outcome: (life, ds) => {
          const cost = checkupCost(life, ds);
          return { moneyDelta: cost > 0 ? -cost : 0, stats: { health: CLINIC_CHECKUP_HEALTH }, logKey: 'place.clinic.checkup.done' };
        },
        available: always,
      },
      {
        id: 'treat',
        labelKey: labelKey('clinic', 'treat'),
        expectKey: expectKey('clinic', 'treat'),
        minigameId: 'clinic',
        // «Очередь» действует только через ожидающее решение health.treatment
        // (decisionOption + флаги treatment*); без него игра — пустая трата очка.
        available: (life) => (life.pendingDecision?.kind === 'health.treatment' ? ok : no(REASON_NO_NEED)),
      },
    ],
  },
  {
    id: 'home',
    labelKey: labelKey('home'),
    group: 'life',
    available: (life) => (life.parentsAlive > 0 || life.partner || life.children > 0 ? ok : no(REASON_NO_FAMILY)),
    actions: [
      {
        id: 'child',
        labelKey: labelKey('home', 'child'),
        expectKey: expectKey('home', 'child'),
        minigameId: 'talk',
        available: (life) => {
          if (life.children <= 0) return no(REASON_NO_FAMILY);
          const youngest = youngestAgeOf(life);
          return youngest !== null && youngest >= TALK_MIN_CHILD_AGE && youngest <= TALK_MAX_CHILD_AGE ? ok : no(REASON_NO_NEED);
        },
      },
      {
        id: 'parents',
        labelKey: labelKey('home', 'parents'),
        expectKey: expectKey('home', 'parents'),
        outcome: () => ({ stats: { relations: HOME_PARENTS_RELATIONS, happiness: HOME_PARENTS_HAPPINESS }, logKey: 'place.home.parents.done' }),
        available: (life) => (life.parentsAlive > 0 ? ok : no(REASON_NO_FAMILY)),
      },
      {
        id: 'partner',
        labelKey: labelKey('home', 'partner'),
        expectKey: expectKey('home', 'partner'),
        outcome: () => ({ stats: { relations: HOME_PARTNER_RELATIONS }, logKey: 'place.home.partner.done' }),
        available: (life) => (life.partner ? ok : no(REASON_NO_FAMILY)),
      },
    ],
  },
  {
    id: 'bar',
    labelKey: labelKey('bar'),
    group: 'life',
    available: (life) => minAge(life, BAR_MIN_AGE),
    actions: [
      {
        id: 'drink',
        labelKey: labelKey('bar', 'drink'),
        expectKey: expectKey('bar', 'drink'),
        expectParams: (life, ds) => ({ p: Math.round(barAlcoholP(ds, life) * 100) }),
        outcome: (life, ds, rng) => {
          const base: MinigameOutcome = { stats: { relations: BAR_RELATIONS, happiness: BAR_HAPPINESS }, logKey: 'place.bar.evening' };
          if (life.habits.alcohol || !rng.chance(barAlcoholP(ds, life))) return base;
          return {
            stats: { relations: BAR_RELATIONS + BAR_ALCOHOL_RELATIONS, happiness: BAR_HAPPINESS, health: BAR_ALCOHOL_HEALTH },
            habits: { alcohol: true },
            logKey: 'place.bar.alcohol',
            heavy: true,
          };
        },
        available: always,
      },
    ],
  },
  {
    id: 'temple',
    labelKey: labelKey('temple'),
    group: 'life',
    available: always,
    actions: [
      {
        id: 'pray',
        labelKey: labelKey('temple', 'pray'),
        expectKey: expectKey('temple', 'pray'),
        outcome: () => ({ stats: { happiness: TEMPLE_HAPPINESS, relations: TEMPLE_RELATIONS }, logKey: 'place.temple.visited' }),
        available: always,
      },
    ],
  },
  {
    id: 'market',
    labelKey: labelKey('market'),
    group: 'money',
    available: (life) => (life.age >= MARKET_MIN_AGE && life.age <= BARGAIN_MAX_AGE ? ok : no(REASON_AGE)),
    actions: [{ id: 'bargain', labelKey: labelKey('market', 'bargain'), expectKey: expectKey('market', 'bargain'), minigameId: 'bargain', available: always }],
  },
  {
    id: 'bank',
    labelKey: labelKey('bank'),
    group: 'money',
    available: (life) => minAge(life, BANK_MIN_AGE),
    actions: [
      {
        id: 'stocks',
        labelKey: labelKey('bank', 'stocks'),
        expectKey: expectKey('bank', 'stocks'),
        minigameId: 'stocks',
        available: (life, ds, law) => (stocks.available(life, ds, law) ? ok : no(REASON_NOT_YET)),
      },
      {
        id: 'crypto',
        labelKey: labelKey('bank', 'crypto'),
        expectKey: expectKey('bank', 'crypto'),
        minigameId: 'crypto',
        available: (life, ds, law) => (crypto.available(life, ds, law) ? ok : no(REASON_NOT_YET)),
      },
      {
        id: 'borrow',
        labelKey: labelKey('bank', 'borrow'),
        expectKey: expectKey('bank', 'borrow'),
        minigameId: 'loans',
        available: (life, ds, law) => (loans.available(life, ds, law) ? ok : no(REASON_NO_NEED)),
      },
    ],
  },
  {
    id: 'travel',
    labelKey: labelKey('travel'),
    group: 'life',
    available: (life) => minAge(life, TRAVEL_MIN_AGE),
    actions: [
      {
        id: 'go',
        labelKey: labelKey('travel', 'go'),
        expectKey: expectKey('travel', 'go'),
        costMoney: travelCost,
        outcome: (life, ds, rng) => {
          const others = listCountries(ds).filter((c) => c.iso3 !== life.iso3);
          const target = others.length > 0 ? rng.pick(others) : null;
          const out: MinigameOutcome = {
            moneyDelta: -travelCost(life, ds),
            stats: { happiness: TRAVEL_HAPPINESS },
            flags: { [FLAG_TRAVELED]: true },
            logKey: 'place.travel.went',
          };
          if (target) out.logParams = { country: target.nameRu };
          return out;
        },
        available: always,
      },
    ],
  },
  {
    id: 'migration',
    labelKey: labelKey('migration'),
    group: 'money',
    available: (life) => minAge(life, MIGRATION_MIN_AGE),
    actions: [
      {
        id: 'learn',
        labelKey: labelKey('migration', 'learn'),
        expectKey: expectKey('migration', 'learn'),
        outcome: (life, ds) => ({
          logKey: 'place.migration.learned',
          logParams: { chance: Math.round(migrationChance(life) * 100), cost: Math.round(ABROAD_COST_GDP * gdpPpp(ds, life)) },
        }),
        available: always,
      },
      {
        id: 'apply',
        labelKey: labelKey('migration', 'apply'),
        expectKey: expectKey('migration', 'apply'),
        expectParams: (life, ds) => ({ need: Math.round(migrationMoneyNeed(life, ds)) }),
        outcome: (life) => ({
          flags: { [FLAG_ABROAD_DECIDED]: false, [FLAG_MIGRATION_APPLIED_AGE]: life.age },
          logKey: 'place.migration.applied',
        }),
        // Зеркало `due` решения move.abroad: иначе подача — пустой no-op на пять лет.
        available: (life, ds) => {
          if (life.age < ABROAD_MIN_AGE || life.age > ABROAD_MAX_AGE) return no(REASON_AGE);
          if (!canAffordMigration(life, ds) && life.flags[FLAG_SPONSOR] !== true) return no(REASON_NO_MONEY);
          const last = num(life, FLAG_MIGRATION_APPLIED_AGE);
          return last !== null && life.age - last < MIGRATION_EVERY_YEARS ? no(REASON_TOO_SOON) : ok;
        },
      },
    ],
  },
  {
    id: 'rest',
    labelKey: labelKey('rest'),
    group: 'life',
    available: always,
    actions: [
      {
        id: 'rest',
        labelKey: labelKey('rest', 'rest'),
        expectKey: expectKey('rest', 'rest'),
        outcome: () => ({ stats: { happiness: POLICY_REST_HAPPINESS }, logKey: 'place.rest.done' }),
        available: always,
      },
    ],
  },
  {
    id: 'casino',
    labelKey: labelKey('casino'),
    group: 'risk',
    available: (life, _ds, law, gamblingEnabled) =>
      gamblingPlace(gamblingEnabled, () => {
        if (life.age < CASINO_MIN_AGE) return no(REASON_AGE);
        if (law !== 'legal') return no(REASON_ILLEGAL);
        return casinoAvailable(life, law) ? ok : no(REASON_CITY);
      }),
    actions: [
      { id: 'roulette', labelKey: labelKey('casino', 'roulette'), expectKey: expectKey('casino', 'roulette'), expectParams: lossExpect(ROULETTE_RTP), minigameId: 'roulette', available: always },
      { id: 'slots', labelKey: labelKey('casino', 'slots'), expectKey: expectKey('casino', 'slots'), expectParams: lossExpect(SLOTS_RTP), minigameId: 'slots', available: always },
      { id: 'blackjack', labelKey: labelKey('casino', 'blackjack'), expectKey: expectKey('casino', 'blackjack'), expectParams: lossExpect(BLACKJACK_RTP_BASIC), minigameId: 'blackjack', available: always },
    ],
  },
  {
    id: 'lottery',
    labelKey: labelKey('lottery'),
    group: 'risk',
    available: (life, _ds, law, gamblingEnabled) =>
      gamblingPlace(gamblingEnabled, () => (life.age < CASINO_MIN_AGE ? no(REASON_AGE) : law === 'illegal' ? no(REASON_ILLEGAL) : ok)),
    actions: [{ id: 'ticket', labelKey: labelKey('lottery', 'ticket'), expectKey: expectKey('lottery', 'ticket'), expectParams: lossExpect(LOTTERY_RTP), minigameId: 'lottery', available: always }],
  },
  {
    id: 'street',
    labelKey: labelKey('street'),
    group: 'risk',
    available: (life, _ds, law, gamblingEnabled) => gamblingPlace(gamblingEnabled, () => minAge(life, DICE_MIN_AGE)),
    actions: [{ id: 'dice', labelKey: labelKey('street', 'dice'), expectKey: expectKey('street', 'dice'), expectParams: lossExpect(1 + DICE_EXPECTATION), minigameId: 'dice', available: always }],
  },
  {
    id: 'bookmaker',
    labelKey: labelKey('bookmaker'),
    group: 'risk',
    available: (life, _ds, law, gamblingEnabled) =>
      gamblingPlace(gamblingEnabled, () => (life.age < CASINO_MIN_AGE ? no(REASON_AGE) : law === 'illegal' ? no(REASON_ILLEGAL) : ok)),
    actions: [{ id: 'bet', labelKey: labelKey('bookmaker', 'bet'), expectKey: expectKey('bookmaker', 'bet'), expectParams: lossExpect(1 - BETS_MARGIN), minigameId: 'bets', available: always }],
  },
];

export function placeDef(id: PlaceId): PlaceDef {
  const def = PLACES.find((p) => p.id === id);
  if (!def) throw new RangeError(`placeDef: unknown place ${id}`);
  return def;
}

/** Общие условия для любого места: жизнь жива, есть энергия, место не исчерпано за год. */
function commonAvailability(place: PlaceDef, life: Life): Availability {
  if (!life.alive) return no(REASON_DEAD);
  if (life.energy <= 0) return no(REASON_NO_ENERGY);
  if (visitMultiplier(life, place.id) === 0) return no(REASON_ENOUGH);
  return ok;
}

function actionOnly(action: PlaceAction, life: Life, ds: Dataset, law: GamblingLaw): Availability {
  const own = action.available(life, ds, law);
  if (!own.ok) return own;
  const cost = action.costMoney?.(life, ds) ?? 0;
  if (cost > 0 && life.money < cost) return no(REASON_NO_MONEY);
  return ok;
}

/**
 * Доступно ли место сейчас: энергия, визиты (`enough`), условия места, и
 * хотя бы одно дело по силам и по деньгам (иначе — причина первого дела).
 */
export function placeAvailability(place: PlaceDef, life: Life, ds: Dataset, opts: PlaceOptions): Availability {
  const common = commonAvailability(place, life);
  if (!common.ok) return common;
  const own = place.available(life, ds, opts.law, opts.gamblingEnabled);
  if (!own.ok) return own;
  let first: Availability | null = null;
  for (const action of place.actions) {
    const a = actionOnly(action, life, ds, opts.law);
    if (a.ok) return ok;
    first ??= a;
  }
  return first ?? no(REASON_NO_NEED);
}

/** Доступно ли конкретное дело: всё, что проверяет `placeAvailability`, плюс условия и цена дела. */
export function actionAvailability(place: PlaceDef, actionId: string, life: Life, ds: Dataset, opts: PlaceOptions): Availability {
  const action = place.actions.find((a) => a.id === actionId);
  if (!action) return no(REASON_NO_NEED);
  const common = commonAvailability(place, life);
  if (!common.ok) return common;
  const own = place.available(life, ds, opts.law, opts.gamblingEnabled);
  if (!own.ok) return own;
  return actionOnly(action, life, ds, opts.law);
}

/**
 * Сходить в место: null, если место или дело недоступно; иначе либо
 * мини-игра (с базовым исходом, если у дела он есть — применить вместе с
 * исходом игры через `mergeOutcomes`), либо готовый исход для `applyActions`.
 * Детерминировано: один seed — один исход.
 */
export function runPlaceAction(place: PlaceId, actionId: string, life: Life, ds: Dataset, rng: Rng, opts: PlaceOptions): PlaceRun | null {
  const def = PLACES.find((p) => p.id === place);
  if (!def) return null;
  const action = def.actions.find((a) => a.id === actionId);
  if (!action) return null;
  if (!actionAvailability(def, actionId, life, ds, opts).ok) return null;
  if (action.minigameId !== undefined && (action.minigameWhen?.(life) ?? true)) {
    const base = action.baseOutcome?.(life, ds, rng);
    return base ? { kind: 'minigame', id: action.minigameId, outcome: base } : { kind: 'minigame', id: action.minigameId };
  }
  const outcome = action.outcome?.(life, ds, rng);
  return outcome ? { kind: 'outcome', outcome } : null;
}
